/*
 * Calendar-day arithmetic for the admin calendar — ROADMAP.md §4.7.
 *
 * Everything here works on `YYYY-MM-DD` strings and does its sums at UTC
 * midnight, which makes it pure date arithmetic: no daylight-saving hour can
 * appear in the middle of a day-count, and no viewer time zone can shift a
 * grid cell. The strings come from and go back to the API unchanged.
 *
 * Weeks start on **Sunday**, matching `WEEKDAY_LABELS` and
 * `preferred_days` — one convention, everywhere.
 */

const DAY_MS = 86_400_000;

function toUtc(day: string): number {
  const parsed = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed)) throw new RangeError(`Not a calendar day: ${day}`);
  return parsed;
}

function fromUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function addDays(day: string, delta: number): string {
  return fromUtc(toUtc(day) + delta * DAY_MS);
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(day: string): number {
  return new Date(toUtc(day)).getUTCDay();
}

export function startOfWeek(day: string): string {
  return addDays(day, -dayOfWeek(day));
}

export function startOfMonth(day: string): string {
  return `${day.slice(0, 8)}01`;
}

export function addMonths(day: string, delta: number): string {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7)) - 1 + delta;
  const rolled = new Date(Date.UTC(year, month, 1));
  return rolled.toISOString().slice(0, 10);
}

/** Every day from `from` to `to`, inclusive. */
export function daysBetween(from: string, to: string): string[] {
  const days: string[] = [];
  for (let at = toUtc(from); at <= toUtc(to); at += DAY_MS) {
    days.push(fromUtc(at));
  }
  return days;
}

/**
 * The 42-cell month grid: the week containing the 1st through the week
 * containing the month's last day, padded to full weeks. Six rows cover every
 * month shape, and a stable row count keeps the layout from jumping as the
 * admin pages through the year.
 */
export function monthGrid(anchor: string): { from: string; to: string; days: string[] } {
  const from = startOfWeek(startOfMonth(anchor));
  const to = addDays(from, 41);
  return { from, to, days: daysBetween(from, to) };
}

export function weekRange(anchor: string): { from: string; to: string; days: string[] } {
  const from = startOfWeek(anchor);
  const to = addDays(from, 6);
  return { from, to, days: daysBetween(from, to) };
}

/** Whether two days fall in the same calendar month. */
export function sameMonth(a: string, b: string): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}
